import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { invalidateAfter, keys } from "../api/query-registry";
import type { AccentName, ThemeName } from "../theme";

export interface Settings {
  theme: ThemeName;
  accent: AccentName;
  digestTime: string;
}

export interface Home {
  id: string;
  name: string;
  sheetUrl: string | null;
  sheetGid: string | null;
  trainingUrl: string | null;
  enabled: boolean;
  hasBridge?: boolean;
  lastSyncedAt?: string | null;
}

// Last-known settings cached in localStorage so a full reload restores the
// theme/accent synchronously instead of flashing the defaults while the
// server round-trips. Server data still wins once loaded.
const SETTINGS_CACHE_KEY = "otdesk.settings";
const HOMES_CACHE_KEY = "otdesk.homes";

export function readCachedSettings(): Settings | null {
  try {
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Settings;
    return s && typeof s.theme === "string" && typeof s.accent === "string" ? s : null;
  } catch {
    return null;
  }
}

function writeCachedSettings(s: Settings): void {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(s));
  } catch {
    /* cache is best-effort; never block settings on storage failure */
  }
}

export function readCachedHomes(): Home[] | null {
  try {
    const raw = localStorage.getItem(HOMES_CACHE_KEY);
    if (!raw) return null;
    const homes = JSON.parse(raw) as Home[];
    return Array.isArray(homes) ? homes : null;
  } catch {
    return null;
  }
}

function writeCachedHomes(homes: Home[]): void {
  try {
    localStorage.setItem(HOMES_CACHE_KEY, JSON.stringify(homes));
  } catch {
    /* cache is best-effort */
  }
}

export function useSettings() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: keys.settings(),
    queryFn: async () => (await api<{ settings: Settings }>("/api/settings")).settings,
    staleTime: Infinity,
    // Render the last-known settings while the fresh fetch is in flight, so the
    // first paint after a reload is correct (no wrong-theme flash).
    placeholderData: readCachedSettings() ?? undefined,
  });
  useEffect(() => {
    if (query.data) writeCachedSettings(query.data);
  }, [query.data]);
  const update = useMutation({
    mutationFn: (patch: Partial<Settings>) =>
      api<{ settings: Settings }>("/api/settings", { method: "PUT", body: patch }).then((r) => r.settings),
    onSuccess: (settings) => {
      qc.setQueryData(keys.settings(), settings);
    },
  });
  return { settings: query.data, isLoading: query.isLoading, isError: query.isError, update };
}

export function useHomes() {
  const query = useQuery({
    queryKey: keys.homes(),
    queryFn: () => api<Home[]>("/api/homes"),
    staleTime: 60_000,
    placeholderData: readCachedHomes() ?? undefined,
  });
  useEffect(() => {
    if (query.data) writeCachedHomes(query.data);
  }, [query.data]);
  return query;
}
/** Update one home's sheet config (Settings > Sheets rows). POST/PATCH -> homesChanged:
 * homeName/sheet fields are serialized into residents, agenda, queue, forecast, day-note. */
export function useUpdateHome(homeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { sheetUrl: string | null; sheetGid: string | null }) =>
      api(`/api/homes/${homeId}`, { method: "PATCH", body: patch }),
    onSuccess: () => invalidateAfter(qc, "homesChanged"),
  });
}